import { useTranslation } from 'react-i18next';
import { UserRoles } from '@bandido/shared/types';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactFlowProvider } from 'reactflow';
import {
  useTheme,
  Box,
  Typography,
  CircularProgress,
  Backdrop,
  Paper,
  IconButton,
  Divider,
  Button,
} from '@mui/material';
import { ArrowBack, Edit } from '@mui/icons-material';

import { useWorkflow } from '@frontend/features/workflows/components/WorkflowContext/hook';
import { WorkflowDiagram } from '@frontend/features/workflows/components/WorkflowDiagram';
import { useGetWorkflowById } from '@frontend/features/workflows/queries';
import { UnsavedChangesModal } from '@frontend/features/workflows/components/UnsavedChangesModal';
import WorkflowContextProvider from '@frontend/features/workflows/components/WorkflowContext';
import { routes } from '../routes';

const WorkflowViewContent = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { myProfile } = useAuth();
  const canViewPerformance = myProfile?.roles?.split(',').includes(UserRoles.ADMINS);
  const { workflowId: id, id: agencyId } = useParams();
  const navigate = useNavigate();
  const { data } = useGetWorkflowById(id);
  const { isLoadingWorkflow } = useWorkflow();

  // TODO: necesitamos mejorar la forma de generar las rutas
  const workflowListRoute = `${routes.dashboard.agencyView.root.replace(':id', String(agencyId))}/${
    routes.dashboard.agencyView.workflows
  }`;

  const handleGoBack = () => {
    navigate(workflowListRoute);
  };

  return (
    <>
      <Backdrop
        open={isLoadingWorkflow}
        style={{
          position: 'absolute',
          height: '100%',
          width: '100%',
          top: 0,
          left: 0,
          backgroundColor: theme.palette.action.disabledBackground,
          zIndex: theme.zIndex.drawer + 1,
          color: theme.palette.primary.main,
        }}
      >
        <Box
          sx={{
            position: 'fixed',
            height: '100vh',
            width: '100vw',
            top: 0,
            left: 0,
            zIndex: theme.zIndex.drawer + 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      </Backdrop>
      {!isLoadingWorkflow && (
        <Box
          display="flex"
          sx={{
            height: 'calc(100vh - 64px)',
            width: '100%',
          }}
        >
          <Paper
            elevation={4}
            sx={{
              backgroundColor: theme.palette.common.white,
              zIndex: 1,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              margin: theme.spacing(3),
              position: 'fixed',
            }}
          >
            <Box p={2} gap={2} display="flex" flexDirection="row" alignItems="center" alignSelf="stretch">
              <IconButton onClick={handleGoBack} sx={{ p: 0.5 }}>
                <ArrowBack color="primary" />
              </IconButton>
              <Divider sx={{ height: 32 }} orientation="vertical" flexItem />
              <Typography variant="subtitle2" color="text.primary">
                {data?.name}
              </Typography>
              {canViewPerformance && (
                <Button onClick={() => navigate(`${workflowListRoute}/${id}/performance`)}>
                  {t('workflowPerformance.configure')}
                </Button>
              )}
              <IconButton sx={{ p: 0.5 }}>
                <Edit color="disabled" />
              </IconButton>
            </Box>
          </Paper>
          <WorkflowDiagram />
        </Box>
      )}
      <UnsavedChangesModal />
    </>
  );
};

const WorkflowView = () => (
  <ReactFlowProvider>
    <WorkflowContextProvider>
      <WorkflowViewContent />
    </WorkflowContextProvider>
  </ReactFlowProvider>
);

export default WorkflowView;
